import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import type { SizeType as AvatarSizeType } from '@atlaskit/avatar/types';
import { Box, Inline, Text } from '@atlaskit/primitives';
import Link from '@atlaskit/link';

import { fg } from '@confluence/feature-gating';
import { ProfileCardWrapper } from '@confluence/profile';
import {
	CURRENT_STATUS,
	DRAFT_STATUS,
	ContentStatus,
} from '@confluence/page-utils/entry-points/enums';
import { useSessionData } from '@confluence/session-data';
import { PEOPLE_DIRECTORY } from '@confluence/named-routes';
import type { TargetContentType } from '@confluence/entity-operations';
import { useCanPerformContentOperation, OperationTypes } from '@confluence/entity-operations';
import { usePageContentId } from '@confluence/page-context';

import {
	ContentOwnershipContextProvider,
	ContentOwnershipContextConsumer,
} from '../ContentOwnershipContext';
import type { ContentOwnershipContextType } from '../ContentOwnershipContext';

import { checkIsChangeOwnerAvailable } from './checkIsChangeOwnerAvailable';
import { ChangeContentOwnerButton } from './ChangeContentOwnerButton';

const i18n = defineMessages({
	owner: {
		id: 'content-ownership.content-owner-item.owner',
		defaultMessage: 'Owner',
		description: 'A label distinguishing the user as the owner of a page',
	},
	creator: {
		id: 'content-ownership.content-owner-item.creator',
		defaultMessage: 'Creator',
		description: 'A label distinguishing the user as the person who created the blogpost',
	},
	ownerOrCreatorWithContributors: {
		id: 'content-ownership.content-owner-item.owner-or-creator-with-contributors',
		defaultMessage:
			'{isBlogpost, select, true {Creator} other {Owner}}{noOtherContributors, select, true {, no contributors} other {}}',
		description:
			'A label showing whether the user is an owner or creator, and if there are other contributors',
	},
	changeOwner: {
		id: 'content-ownership.content-owner-item.change-owner',
		defaultMessage: 'Change owner',
		description: 'An icon label within a button used to change the owner of a page',
	},
	accessibleAvatarLabel: {
		id: 'content-ownership.content-owner-item.accessible-avatar-label',
		defaultMessage: '{displayName}’s profile picture',
		description: "Alt text and aria-label for a user's profile picture",
	},
});

type ContentOwnerItemWithChangeOwnerProps = {
	ownerAccountId?: string | null;
	ownerName?: string | null;
	ownerAvatarUrl?: string | null;
	contentType?: string | null;
	contentStatus?: ContentStatus | string | null;
	noOtherContributors?: boolean;
	isCurrentUserSpaceAdmin: boolean;
	avatarSize?: AvatarSizeType;
	analyticsSource?: string | null;
	// use this if you need to render the change owner modal from a higher level in the tree. example: the button is in a dropdown that closes when the modal opens.
	overrideChangeOwnerClick?: () => void;
};

const noop = () => {};

export const ContentOwnerItemWithChangeOwner: FC<ContentOwnerItemWithChangeOwnerProps> = ({
	ownerAccountId,
	ownerName,
	ownerAvatarUrl,
	contentType,
	contentStatus,
	noOtherContributors,
	isCurrentUserSpaceAdmin,
	avatarSize = 'large',
	analyticsSource,
	overrideChangeOwnerClick,
}) => {
	const { formatMessage } = useIntl();
	const { userId: currentUserId } = useSessionData();

	const [contentId] = usePageContentId();
	const [canUpdateContent] = useCanPerformContentOperation({
		contentId: contentId ?? '',
		contentType: contentType as TargetContentType,
		operation: OperationTypes.UPDATE,
		status: contentStatus === ContentStatus.DRAFT ? DRAFT_STATUS : CURRENT_STATUS,
	});

	const getUserTitle = () => {
		// The concept of "ownership" doesn't apply to blogposts for some reason, so blogposts should refer to "creator"
		const isBlogpost = contentType === 'blogpost';
		if (fg('page_byline_owner_and_contributors_dropdown')) {
			return formatMessage(i18n.ownerOrCreatorWithContributors, {
				isBlogpost,
				noOtherContributors,
			});
		}
		return isBlogpost ? formatMessage(i18n.creator) : formatMessage(i18n.owner);
	};

	const ownerProfileUrl = ownerAccountId ? `${PEOPLE_DIRECTORY.toUrl()}/${ownerAccountId}` : '';
	const isChangeOwnerAvailable = checkIsChangeOwnerAvailable({
		contentStatus,
		contentType,
		isContentOwner: ownerAccountId === currentUserId,
		isSpaceAdmin: isCurrentUserSpaceAdmin,
		// pass true if FF is off or canUpdateContent is undefined
		hasWriteAccess: fg('confluence_frontend_content_wrapper') ? canUpdateContent ?? true : true,
	});

	const renderChangeOwnerButton = () => {
		if (overrideChangeOwnerClick) {
			return (
				<ChangeContentOwnerButton
					onClick={overrideChangeOwnerClick}
					analyticsContext={{
						source: analyticsSource,
						attributes: { role: 'owner', name: 'changeOwner', override: 'true' },
					}}
				/>
			);
		}

		return (
			<ContentOwnershipContextProvider>
				<ContentOwnershipContextConsumer>
					{({ openModal }: ContentOwnershipContextType) => {
						return (
							<ChangeContentOwnerButton
								onClick={openModal}
								analyticsContext={{
									source: analyticsSource,
									attributes: { role: 'owner', name: 'changeOwner', override: 'false' },
								}}
							/>
						);
					}}
				</ContentOwnershipContextConsumer>
			</ContentOwnershipContextProvider>
		);
	};

	return (
		<Inline spread="space-between" alignBlock="center" space="space.100">
			<Inline alignBlock="center" space="space.100">
				<ProfileCardWrapper userId={ownerAccountId}>
					<Avatar
						size={avatarSize}
						borderColor="transparent"
						href={ownerProfileUrl}
						src={ownerAvatarUrl ?? undefined}
						name={formatMessage(i18n.accessibleAvatarLabel, {
							displayName: ownerName,
						})}
						onClick={
							/* an analytics event is only fired if onClick is provided,
							but providing onClick will change behavior if href is not provided */
							ownerProfileUrl ? noop : undefined
						}
						analyticsContext={{
							source: analyticsSource,
							attributes: { role: 'owner', name: 'profileImage' },
						}}
					/>
				</ProfileCardWrapper>
				<Box>
					<Box>
						<Link
							appearance="subtle"
							href={ownerProfileUrl}
							analyticsContext={{
								source: analyticsSource,
								attributes: { role: 'owner', name: 'profileName' },
							}}
						>
							{ownerName}
						</Link>
					</Box>
					<Inline>
						<Text size="small" color="color.text.subtlest">
							{getUserTitle()}
						</Text>
					</Inline>
				</Box>
			</Inline>
			{isChangeOwnerAvailable && renderChangeOwnerButton()}
		</Inline>
	);
};
